import React from 'react';

import { TBreadcrumb } from '@/shared/features/header/components/breadcrumbs/breadcrumbs.types';
import { useTypedMatches } from '@/shared/hooks/use-typed-matches/use-typed-matches';
import { TCustomMatch } from '@/shared/hooks/use-typed-matches/use-typed-matches.types';
import { TCrumb } from '@/shared/types/router.types';

/**
 * Resolves a value that can be either a direct value or a function returning that value
 * @param property The property which might be a function or direct value
 * @param data Data to pass to the function if property is a function
 * @returns The resolved value
 */
const resolveProperty = <T>(
  property: T | ((...args: any[]) => T) | undefined,
  data: unknown,
): T | undefined => {
  if (property && typeof property === 'function') {
    // Use type assertion to tell TypeScript this is definitely a function
    return (property as (...args: any[]) => T)(data);
  }
  return property;
};

/**
 * Checks if a value is a valid TCrumb object (not a React node)
 */
const isCrumbObject = (ancestor: unknown): ancestor is TCrumb => {
  return (
    ancestor !== null &&
    typeof ancestor === 'object' &&
    !React.isValidElement(ancestor)
  );
};

/**
 * Recursively processes crumbs and their ancestors
 * @param crumb The crumb to process
 * @param data Data for resolving functions
 * @param pathname Default pathname to use as link
 * @returns Array of breadcrumbs and React nodes in order from oldest ancestor to current
 */
const processAncestryChain = (
  crumb: TCrumb,
  data: unknown,
  pathname?: string,
): (TBreadcrumb | React.ReactNode)[] => {
  // Start with an empty array for the ancestry chain
  const result: (TBreadcrumb | React.ReactNode)[] = [];

  // Process any ancestor first
  const ancestor = resolveProperty(crumb.ancestor, data);

  if (ancestor) {
    if (React.isValidElement(ancestor)) {
      // For React elements, just add them directly
      result?.push(ancestor);
    } else if (isCrumbObject(ancestor)) {
      // For crumb objects, process recursively to handle nested ancestors
      result?.push(...processAncestryChain(ancestor, data));
    }
  }

  // Process the current crumb
  const label = resolveProperty(crumb.label, data);

  // Only add the current crumb if it has a valid label
  if (label) {
    const localized = Boolean(crumb.localized);
    const link = resolveProperty(crumb.link, data) || pathname;

    result?.push({ label, localized, link });
  }

  return result;
};

export const useBreadcrumbs = (): (TBreadcrumb | React.ReactNode)[] => {
  const matches = useTypedMatches();

  const createBreadcrumbFromMatch = (
    match: TCustomMatch,
  ): (TBreadcrumb | React.ReactNode)[] => {
    const crumb = match.handle?.crumb;

    if (!crumb) {
      console.warn(
        'No crumb name provided. Did you forget to set one in the routes?',
      );
      return [];
    }

    // Process the entire ancestry chain for this match
    return processAncestryChain(crumb, match.loaderData, match.pathname);
  };

  // Build complete breadcrumb trail from all matches
  return matches.flatMap(createBreadcrumbFromMatch);
};
